// React controls shared by the parent app and the admin. Styles are in styles/base.css (.select-*, .theme-toggle).
import { Children, isValidElement, useId, type ReactElement, type ReactNode } from 'react';
import * as RS from '@radix-ui/react-select';
import { ICONS, THEMES } from '../src/icons';

const Icon = ({ svg, className }: { svg: string; className?: string }) => (
  <span className={className} aria-hidden="true" dangerouslySetInnerHTML={{ __html: svg }} />
);

// Radix items cannot have an empty value, so "" (an "any" or "none" choice) travels as this instead.
const EMPTY = '__none__';
const enc = (v: string) => (v === '' ? EMPTY : v);
const dec = (v: string) => (v === EMPTY ? '' : v);

type OptionProps = { value?: string | number; disabled?: boolean; children?: ReactNode };

/** Reads <option> children (through fragments and arrays) so call sites keep the familiar markup. */
function readOptions(children: ReactNode): { value: string; label: ReactNode; disabled?: boolean }[] {
  const out: { value: string; label: ReactNode; disabled?: boolean }[] = [];
  Children.forEach(children, (c) => {
    if (!isValidElement(c)) return;
    const el = c as ReactElement<OptionProps>;
    if (el.type === 'option') out.push({ value: String(el.props.value ?? ''), label: el.props.children, disabled: el.props.disabled });
    else if (el.props.children) out.push(...readOptions(el.props.children));
  });
  return out;
}

export type SelectProps = {
  value: string | number;
  onChange: (value: string) => void;
  children: ReactNode;
  label?: string;
  hint?: ReactNode;
  'aria-label'?: string;
  size?: 'sm' | 'md';
  disabled?: boolean;
};

/** A styled, accessible dropdown. With a label it renders as a form field; without one, pass aria-label. */
export function Select({ value, onChange, children, label, hint, size = 'md', disabled, ...rest }: SelectProps) {
  const id = useId();
  const options = readOptions(children);
  const control = (
    <RS.Root value={enc(String(value))} onValueChange={(v) => onChange(dec(v))} disabled={disabled}>
      <RS.Trigger
        id={id}
        className={`input select-trigger${size === 'sm' ? ' input-sm' : ''}`}
        aria-label={rest['aria-label']}
        aria-describedby={hint ? `${id}-h` : undefined}
      >
        <span className="select-value"><RS.Value /></span>
        <RS.Icon asChild><Icon svg={ICONS.chevron} className="select-chevron" /></RS.Icon>
      </RS.Trigger>
      <RS.Portal>
        <RS.Content className="select-panel" position="popper" sideOffset={6} collisionPadding={12}>
          <RS.ScrollUpButton className="select-scroll"><Icon svg={ICONS.chevron} className="flip" /></RS.ScrollUpButton>
          <RS.Viewport className="select-viewport">
            {options.map((o) => (
              <RS.Item key={o.value} value={enc(o.value)} disabled={o.disabled} className="select-item">
                <RS.ItemText>{o.label}</RS.ItemText>
                <RS.ItemIndicator className="select-check"><Icon svg={ICONS.check} /></RS.ItemIndicator>
              </RS.Item>
            ))}
          </RS.Viewport>
          <RS.ScrollDownButton className="select-scroll"><Icon svg={ICONS.chevron} /></RS.ScrollDownButton>
        </RS.Content>
      </RS.Portal>
    </RS.Root>
  );
  if (!label) return control;
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {control}
      {hint && <div id={`${id}-h`} className="hint">{hint}</div>}
    </div>
  );
}

export type Theme = 'light' | 'dark';

/** Match device / light / dark as a small segmented icon switch with a sliding highlight. */
export function ThemeToggle({ value, onChange }: { value: Theme; onChange: (t: Theme) => void }) {
  return (
    <div className="theme-toggle" data-value={value} role="radiogroup" aria-label="Color theme">
      <span className="knob" aria-hidden="true" />
      {THEMES.map((t) => (
        <button key={t.value} type="button" role="radio" aria-checked={value === t.value} aria-label={t.label} title={t.label} onClick={() => onChange(t.value)}>
          <Icon svg={t.icon} />
        </button>
      ))}
    </div>
  );
}
